import React, { useMemo } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { digits, isMynavi } from '@this/src/util';
import type { SelectType, Airline } from '../../../../../domain/select_store';
import type SelectStore from '../../../../../domain/select_store';

interface Props {
  store: SelectStore;
  width: number;
  handleSelectType: (type: SelectType) => () => void;
  handleAirline: (airline: Airline) => () => void;
  serviceId: number;
}

const CheapestPriceLabel = ({ styles }: { styles: ReturnType<typeof getStyles> }) => (
  <div style={styles.cheapestPrice}>最安値プランあり</div>
);

const PriceDisplay = ({ price, styles }: { price: number | null; styles: ReturnType<typeof getStyles> }) => (
  <span style={styles.yen} data-wovn-ignore>
    {price ? digits(price) : 'なし'}
  </span>
);

const LoadingDisplay = ({ styles }: { styles: ReturnType<typeof getStyles> }) => (
  <span style={styles.yen} data-wovn-ignore>
    <div data-wovn-ignore className="cssloader">
      Loading...
    </div>
  </span>
);

type PackageType = Airline | 'JR';

interface PackageTabProps {
  packageType: PackageType;
  store: SelectStore;
  serviceId: number;
  selected: boolean;
  onSelect: () => void;
  visible?: boolean;
  styles: ReturnType<typeof getStyles>;
}

const isAirlinePackage = (type: PackageType): type is Airline => {
  return type === 'ANA' || type === 'JAL';
};

// MEMO: 用途が増えたらファイル分割する
const PackageTab = ({
  packageType,
  store,
  serviceId,
  selected,
  onSelect,
  visible = true,
  styles
}: PackageTabProps) => {
  if (visible === false) return null;

  const isAirline = isAirlinePackage(packageType);

  const isLoading = (() => {
    if (packageType === 'ANA') return store.searchProgressAna();
    if (packageType === 'JAL') return store.searchProgressJal();
    return store.searchProgressJr();
  })();

  const getPackagePrice = (): number => {
    if (isAirline) {
      return store.result.getAirPackageTrip(packageType).packageFinalPrice();
    }
    return store.result.getJrPackageTrip().packageFinalPrice();
  };

  const getCheapestPrice = (): number => {
    if (isAirline) {
      return store.result.getAirPackageTripCheapest(packageType).packageFinalPrice();
    }
    return store.result.getJrPackageTripCheapest().packageFinalPrice();
  };

  const isShortestPrice =
    store.searchProgressAll(serviceId) === false && store.cheapestPricePackage(serviceId) === packageType;

  const renderContent = () => {
    if (isLoading) {
      return (
        <>
          {packageType}パッケージ
          <br />
          <LoadingDisplay styles={styles} />
        </>
      );
    }

    const price = getPackagePrice();
    if (price === 0) {
      return (
        <>
          {packageType}パッケージ
          <br />
          <span style={styles.yen}>なし</span>
        </>
      );
    }

    const cheapestPrice = getCheapestPrice();
    return (
      <>
        {packageType}パッケージ
        <br />
        <PriceDisplay price={cheapestPrice} styles={styles} />
        <span style={styles.yen}>円〜</span>
      </>
    );
  };

  return (
    <div
      style={{
        ...styles.tab.base,
        ...(selected ? styles.tab.selected : {})
      }}
      onClick={onSelect}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      role="button"
      tabIndex={0}
    >
      {isShortestPrice && <CheapestPriceLabel styles={styles} />}
      {renderContent()}
    </div>
  );
};

const TripPlanSelector = ({ store, handleSelectType, handleAirline, width, serviceId }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);

  if (!store.showPackage || !store.result.isPackageSupportSearchType()) {
    return null;
  }

  return (
    <div style={styles.top(width)}>
      <div style={styles.tabs}>
        <div
          style={{
            ...styles.tab.base,
            ...(store.result.type === 'separate' ? styles.tab.selected : {})
          }}
          onClick={handleSelectType('separate')}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleSelectType('separate')();
            }
          }}
          role="button"
          tabIndex={0}
        >
          個別プラン
          <br />
          <span style={styles.yen} data-wovn-ignore>
            {digits(store.result.getReservingTrip().separateFinalPrice())}
          </span>
          <span style={styles.yen}>円〜</span>
        </div>

        <PackageTab
          packageType="ANA"
          store={store}
          serviceId={serviceId}
          selected={
            store.result.type !== 'separate' &&
            store.result.airline === 'ANA' &&
            store.result.type === 'airPackage'
          }
          onSelect={handleAirline('ANA')}
          styles={styles}
        />

        <PackageTab
          packageType="JAL"
          store={store}
          serviceId={serviceId}
          selected={
            store.result.type !== 'separate' &&
            store.result.airline === 'JAL' &&
            store.result.type === 'airPackage'
          }
          onSelect={handleAirline('JAL')}
          styles={styles}
        />

        <PackageTab
          packageType="JR"
          store={store}
          serviceId={serviceId}
          selected={store.result.type !== 'separate' && store.result.type === 'jrPackage'}
          onSelect={handleSelectType('jrPackage')}
          visible={isMynavi(serviceId) === false}
          styles={styles}
        />
      </div>

      {store.isEndFlashMsg() === false && (
        <div style={styles.loadingTab}>
          {store.searchProgressAll(serviceId) ? (
            <>
              最適なパッケージを検索してます{' '}
              <span style={styles.loadingSpan}>
                <div data-wovn-ignore className="cssloader">
                  Loading...
                </div>
              </span>
            </>
          ) : (
            <>{store.searchPackageResultText(serviceId)}</>
          )}
        </div>
      )}
    </div>
  );
};

function getStyles(theme: AITTheme) {
  return {
    top: (width: number): React.CSSProperties => ({
      width: `${width}%`,
      padding: '0px 10px 0px 10px'
    }),
    tabs: {
      display: 'flex'
    } as React.CSSProperties,
    tab: {
      base: {
        border: '1px solid #e5e7eb',
        textAlign: 'center' as const,
        fontSize: '11px',
        flexShrink: 0,
        flexBasis: 'auto',
        padding: '16px 8px',
        lineHeight: '110%',
        fontWeight: 'normal',
        cursor: 'pointer',
        color: '#374151',
        marginTop: '-1px',
        marginLeft: '-1px',
        width: 'calc(25% + 1px)',
        position: 'relative'
      } as React.CSSProperties,
      selected: {
        border: '1px solid #e5e7eb',
        backgroundColor: theme.linkColor,
        cursor: 'default',
        color: 'white'
      } as React.CSSProperties
    },
    yen: {
      fontSize: '13px',
      fontWeight: 'bold',
      lineHeight: '125%'
    } as React.CSSProperties,
    loadingTab: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: '#F8F6F1',
      textAlign: 'center' as const,
      verticalAlign: 'middle',
      fontSize: '13px',
      fontWeight: 'bold',
      lineHeight: '110%',
      color: '#374151',
      padding: '20px 20px',
      width: '100%',
      marginTop: '5px'
    } as React.CSSProperties,
    loadingSpan: {
      width: '20%',
      top: '-0.5em'
    } as React.CSSProperties,
    cheapestPrice: {
      border: '1px solid #ff0000',
      backgroundColor: '#ffffff',
      color: '#ff0000',
      fontSize: '8px',
      padding: '3px',
      borderRadius: '10px',
      top: '-10px',
      left: '15%',
      position: 'absolute' as const,
      zIndex: 10
    } as React.CSSProperties
  } as const;
}

export default TripPlanSelector;
